/**
 * Curated static URL → title/description for high-value indexable paths.
 * See AGENTS.md (SEO title & meta description) for the copy formula.
 *
 * Titles follow: {primary keywords} | {intent or value} | JobCrawls
 * Descriptions: keyword echo + aggregation value + freshness + soft CTA (~145–160 chars).
 *
 * IMPORTANT: a large share of the keys below are DEAD CODE on production. CloudFront routes
 * `/`, `/en`, `/fi`, `/sv`, the about/privacy/terms/partnerships pages, and the entire resources
 * hub (`/en/resources*`, `/fi/tyoelama*`, `/sv/arbetsliv*`) straight to the web-static S3
 * prerender (see `web-app/cdk/lib/vike-stack.ts`'s `addBehavior(..., assetOrigin, ...)` calls,
 * around line 1682 onward) — this file's `getStaticUrlSeoOverride` lookup, called from
 * `web-app/pages/index/+title.ts`, never runs for those paths, so editing their entries here has
 * no effect on the live site. Their real, working titles live in web-static instead, one
 * `+title.ts` per page directory (e.g. `web-static/pages/landing/seo-meta.ts` for the homepages,
 * `web-static/pages/tyoelama/+title.ts` for the fi resource hub). Verified live 2026-09-04 by
 * curling production and diffing against both sources.
 *
 * The entries that ARE live (still routed to the web-app Lambda/SSR origin, so this file is the
 * real source of truth): the two curated role pages, `/en/locations` + fi/sv, and `/en/employers`
 * + fi/sv. City pages have no rows on purpose: they use the generated title with the live job
 * count (generate-srp-seo-meta.ts). `/en/for-employers` + fi/sv is NOT live from here despite the
 * entry below existing in this file; that page owns its
 * own title/description in `web-app/pages/for-employers/+title.ts` and `+Head.tsx`, which never
 * read this file. When adding a new key here, check the CDK behavior list first — if the exact
 * path (or a matching wildcard) is routed to `assetOrigin`, this file is the wrong place for it.
 */

export interface UrlStaticSeoMeta {
  title: string;
  description: string;
}

/** Curated copy for the English marketing home (`/` and legacy `/en` lookup). */
const EN_MARKETING_HOME_SEO: UrlStaticSeoMeta = {
  title: "Job Search in Finland | Track Every Application | JobCrawls",
  description:
    "JobCrawls aggregates jobs from Finnish and international boards in one search and lets you track every application in one place. Built for serious job seekers, not recruiters.",
};

/** Path keys are pathname-only (no domain), no trailing slash; `/` and `/en` use the same English home row. */
export const STATIC_URL_SEO_META: Record<string, UrlStaticSeoMeta> = {

  // ---------------------------------------------------------------------------
  // HOMEPAGES
  // ---------------------------------------------------------------------------

  "/": EN_MARKETING_HOME_SEO,

  "/en": EN_MARKETING_HOME_SEO,

  "/fi": {
    title: "Työnhaku yhdellä haulla | Hakemusten seuranta | JobCrawls",
    description:
      "JobCrawls kokoaa avoimet työpaikat yritysten urasivuilta, Työmarkkinatorilta ja kansainvälisistä lähteistä yhteen hakuun. Seuraa hakemuksiasi samassa palvelussa.",
  },

  "/sv": {
    title: "Jobbsök i Finland | Följ dina ansökningar | JobCrawls",
    description:
      "JobCrawls samlar jobb från finska och internationella jobbsajter i en sökning och låter dig följa alla ansökningar på ett ställe. Byggt för seriösa jobbsökare, inte rekryterare.",
  },

  // ---------------------------------------------------------------------------
  // ROLE PAGES (Aug 2026 search report, cluster 6: healthcare only)
  // ---------------------------------------------------------------------------

  "/en/jobs/role/lahihoitaja": {
    // Two segments, matching fi/sv below and the company-page precedent documented in
    // AGENTS.md: the role name is the query, so it doesn't need a second differentiating segment.
    title: "Practical Nurse Jobs in Finland | JobCrawls",
    description:
      "Find practical nurse (lähihoitaja) jobs from company career pages and Työmarkkinatori in one search. Elderly care, home care and disability services, updated daily.",
  },

  "/sv/lediga-jobb/yrke/lahihoitaja": {
    title: "Lediga närvårdarjobb i Finland | JobCrawls",
    description:
      "Hitta lediga jobb som närvårdare från företags karriärsidor och Työmarkkinatori i en sökning. Äldreomsorg, hemvård och handikappservice, uppdateras dagligen.",
  },

  "/en/jobs/role/sairaanhoitaja": {
    // Two segments, matching fi/sv below (see the /lahihoitaja entry above for why).
    title: "Registered Nurse Jobs in Finland | JobCrawls",
    description:
      "Find registered nurse (sairaanhoitaja) jobs from company career pages and Työmarkkinatori in one search. Hospital, clinic and home care nursing, updated daily.",
  },

  "/sv/lediga-jobb/yrke/sairaanhoitaja": {
    title: "Lediga sjuksköterskejobb i Finland | JobCrawls",
    description:
      "Hitta lediga sjuksköterskejobb från företags karriärsidor och Työmarkkinatori i en sökning. Sjukhus, hälsostationer och hemsjukvård, uppdateras dagligen.",
  },

  // ---------------------------------------------------------------------------
  // STATIC PAGES
  // ---------------------------------------------------------------------------

  "/en/locations": {
    title: "Job Locations in Finland | Browse by City | JobCrawls",
    description:
      "Browse open jobs by city across Finland. Helsinki, Tampere, Turku, Oulu and every major Finnish city covered with live listings aggregated from company career pages and Työmarkkinatori, updated daily.",
  },

  "/fi/sijainnit": {
    title: "Työpaikkojen sijainnit Suomessa | Selaa kaupungeittain | JobCrawls",
    description:
      "Selaa avoimia työpaikkoja kaupungeittain: Helsinki, Tampere, Turku, Oulu ja muut suuret kaupungit. Tuoreet ilmoitukset yritysten urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/platser": {
    title: "Jobborter i Finland | Bläddra efter stad | JobCrawls",
    description:
      "Bläddra bland lediga jobb stad för stad i hela Finland. Helsingfors, Tammerfors, Åbo och Uleåborg med dagliga uppdateringar från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/employers": {
    title: "Best Companies to Work for in Finland | Ranked by Hiring Activity | JobCrawls",
    description:
      "Discover Finland's top employers ranked by active hiring and employee reputation. Compare companies across tech, healthcare, engineering and finance. Find where you actually want to work.",
  },

  "/fi/tyonantajat": {
    title: "Parhaat työnantajat Suomessa | Rekrytoivat nyt | JobCrawls",
    description:
      "Suomen arvostetuimmat työnantajat rekrytointiaktiivisuuden ja työntekijämaineen perusteella. Vertaile yrityksiä teknologia-, terveys- ja insinöörialoilla.",
  },

  "/sv/alla-arbetsgivare": {
    title: "Bästa företagen i Finland | Rankade efter rekryteringsaktivitet | JobCrawls",
    description:
      "Utforska Finlands främsta arbetsgivare rankade efter aktiv rekrytering och anseende. Jämför företag inom teknik, hälso- och sjukvård, ingenjörstjänster och finans.",
  },

  "/en/for-employers": {
    title: "For Employers | Reach Finland's Most Active Job Seekers | JobCrawls",
    description:
      "JobCrawls puts your roles in front of job seekers searching company career pages and Työmarkkinatori: reach Finland's most active candidates.",
  },

  "/fi/tyonantajille": {
    title: "Työnantajille | Tavoita aktiiviset työnhakijat | JobCrawls",
    description:
      "JobCrawls kokoaa yhteen työnhakijat, jotka etsivät töitä urasivuilta ja Työmarkkinatorilta. Katso, miten saat ilmoituksesi Suomen aktiivisimpien hakijoiden eteen.",
  },

  "/sv/for-arbetsgivare": {
    title: "För arbetsgivare | Nå Finlands mest aktiva jobbsökare | JobCrawls",
    description:
      "JobCrawls aggregerar jobbsökare som söker på företags karriärsidor och Työmarkkinatori på ett ställe. Lär dig hur du får dina annonser framför Finlands mest aktiva kandidater.",
  },

  "/en/partnerships": {
    title: "Partnerships | Distribute Your Jobs Across Finland | JobCrawls",
    description:
      "Partner with JobCrawls to reach job seekers across Finland from one integration. Distribution partnerships, data partnerships and job board syndication available.",
  },

  "/fi/kumppanuudet": {
    title: "Kumppanuudet | Työpaikkasi suomalaisille alustoille | JobCrawls",
    description:
      "Tee yhteistyötä JobCrawlsin kanssa ja tavoita työnhakijat ympäri Suomen yhdellä integraatiolla. Jakeluyhteistyö, datayhteistyö ja syndikointi.",
  },

  "/sv/partnerskap": {
    title: "Partnerskap | Distribuera dina jobb till hela Finland | JobCrawls",
    description:
      "Samarbeta med JobCrawls för att nå jobbsökare i hela Finland från en integration. Distributionspartnerskap, datapartnerskap och jobbannonssyndikering tillgängligt.",
  },

  "/en/about": {
    title: "About JobCrawls | Built for Serious Job Seekers | JobCrawls",
    description:
      "JobCrawls exists because serious job seekers need more than one job board. We aggregate Finnish and international listings into one search and let you track every application in one place.",
  },

  "/fi/tietoa-jobcrawlsista": {
    title: "Tietoa JobCrawlsista | Tehty vakavaan työnhakuun | JobCrawls",
    description:
      "JobCrawls on tehty tosissaan hakeville. Kokoamme suomalaiset ja kansainväliset työpaikat yhteen hakuun ja annamme työkalut hakemusten seurantaan.",
  },

  "/sv/om-jobcrawls": {
    title: "Om JobCrawls | Byggt för seriösa jobbsökare | JobCrawls",
    description:
      "JobCrawls är byggt för jobbsökare som söker på allvar. Vi aggregerar finska och internationella jobb i en sökning och låter dig följa alla dina ansökningar på ett ställe.",
  },

  "/en/privacy": {
    title: "Privacy Policy | How We Handle Your Data | JobCrawls",
    description:
      "Read JobCrawls' privacy policy. We explain clearly what data we collect, how it is used, and how you can control it. GDPR compliant and based in Finland.",
  },

  "/fi/tietosuoja": {
    title: "Tietosuojaseloste | Miten käsittelemme tietojasi | JobCrawls",
    description:
      "Lue JobCrawlsin tietosuojaseloste: mitä tietoja keräämme, mihin käytämme niitä ja miten voit hallita niitä. GDPR:n mukainen, Suomessa toimiva palvelu.",
  },

  "/sv/integritet": {
    title: "Integritetspolicy | Hur vi hanterar dina uppgifter | JobCrawls",
    description:
      "Läs JobCrawls integritetspolicy. Vi förklarar tydligt vilka uppgifter vi samlar in, hur de används och hur du kan kontrollera dem. GDPR-kompatibel tjänst baserad i Finland.",
  },

  "/en/terms": {
    title: "Terms of Service | Using JobCrawls | JobCrawls",
    description:
      "Read the terms governing your use of JobCrawls. Covers job seeker use, employer access, data usage and our aggregation of company career page and Työmarkkinatori listings.",
  },

  "/fi/kayttoehdot": {
    title: "Käyttöehdot | JobCrawlsin käyttäminen | JobCrawls",
    description:
      "Lue JobCrawlsin käyttöehdot: palvelun käyttö työnhakijana ja työnantajana, tietojen käyttö sekä urasivujen ja Työmarkkinatorin ilmoitusten kokoaminen yhteen.",
  },

  "/sv/villkor": {
    title: "Användningsvillkor | Att använda JobCrawls | JobCrawls",
    description:
      "Läs villkoren för din användning av JobCrawls. Täcker jobbsökares användning, arbetsgivaråtkomst, dataanvändning och vår aggregering av annonser från företags karriärsidor och Työmarkkinatori.",
  },

  // ---------------------------------------------------------------------------
  // RESOURCES HUB
  // ---------------------------------------------------------------------------

  "/en/resources": {
    title: "Job Search Guides for Finland | For Serious Job Seekers | JobCrawls",
    description:
      "Salary benchmarks, job market data, CV guides and application strategy for people running a serious job search in Finland. Written for seekers who want the full picture, not just a list of jobs.",
  },

  "/fi/tyoelama": {
    title: "Työnhakuoppaat Suomessa | Vakavaan työnhakuun | JobCrawls",
    description:
      "Palkkatietoja, työmarkkinakatsauksia, CV-vinkkejä ja hakustrategiaa työnhakijoille, jotka hakevat tosissaan ja haluavat kokonaiskuvan, ei pelkkää ilmoituslistaa.",
  },

  "/sv/arbetsliv": {
    title: "Jobbsökarguider för Finland | För seriösa jobbsökare | JobCrawls",
    description:
      "Lönedata, arbetsmarknadsanalyser, CV-tips och sökstrategi för dig som söker jobb på allvar i Finland. Skrivet för dem som vill ha hela bilden, inte bara en lista med jobb.",
  },

  // ---------------------------------------------------------------------------
  // RESOURCE ARTICLES
  // ---------------------------------------------------------------------------

  "/en/resources/remote-work-in-finland": {
    title: "Remote Work in Finland | What Job Seekers Need to Know | JobCrawls",
    description:
      "Which sectors actually hire remote in Finland, what Finnish employers expect from remote workers, and where to find the roles, all in one place instead of hopping between job boards.",
  },

  "/fi/tyoelama/etatyo-suomessa": {
    title: "Etätyö Suomessa | Mitä työnhakijan tulee tietää | JobCrawls",
    description:
      "Etätyö Suomessa: millä aloilla palkataan etätöihin, mitä suomalaiset työnantajat odottavat ja mistä löydät parhaat etätyöpaikat yhdellä haulla.",
  },

  "/sv/arbetsliv/distansarbete-i-finland": {
    title: "Distansarbete i Finland | Vad jobbsökare behöver veta | JobCrawls",
    description:
      "Allt du behöver veta om distansarbete i Finland: vilka sektorer rekryterar på distans, vad finska arbetsgivare förväntar sig och var du hittar de bästa distansjobben i en sökning.",
  },

  "/en/resources/salary-transparency-in-finnish-job-listings": {
    title: "Salary Transparency in Finnish Job Listings | What to Expect | JobCrawls",
    description:
      "How common is salary information in Finnish job ads and what does the EU Pay Transparency Directive mean for job seekers in Finland? We analysed listings from company career pages and Työmarkkinatori.",
  },

  "/fi/tyoelama/palkkalapinakyvyys-suomalaisissa-tyopaikkailmoituksissa": {
    title: "Palkkaläpinäkyvyys ilmoituksissa | Kuinka yleistä | JobCrawls",
    description:
      "Kuinka moni suomalainen työpaikkailmoitus kertoo palkan ja mitä EU:n palkkaläpinäkyvyysdirektiivi tarkoittaa työnhakijalle? Data urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/lonetransparens-i-finska-jobbannonser": {
    title: "Lönetransparens i finska jobbannonser | Vad du kan förvänta dig | JobCrawls",
    description:
      "Hur vanligt är det med löneinformation i finska jobbannonser och vad innebär EU:s lönetransparensdirektiv för jobbsökare i Finland? Vi analyserade annonser från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/finland-salary-transparency-law-2026": {
    title: "Finland Salary Transparency Law 2026 | What Changes for Job Seekers | JobCrawls",
    description:
      "From May 2026, Finnish employers must share salary ranges early in hiring. We break down which ones are affected, how disclosure will change, and what you can actually expect as a job seeker.",
  },

  "/fi/tyoelama/palkkalapinakyvyys-laki-suomi-2026": {
    title: "Palkkaläpinäkyvyyslaki 2026 | Mitä muuttuu hakijalle | JobCrawls",
    description:
      "EU:n palkkaläpinäkyvyysdirektiivi astuu voimaan 2026. Mitä se tarkoittaa työnhakijalle Suomessa, mitä työnantajia se koskee ja miten palkan kertominen muuttuu.",
  },

  "/sv/arbetsliv/lonetransparens-lag-finland-2026": {
    title: "Lönetransparenslagen Finland 2026 | Vad ändras för jobbsökare | JobCrawls",
    description:
      "EU:s lönetransparensdirektiv träder i kraft 2026. Här är vad det innebär för jobbsökare i Finland, vilka arbetsgivare berörs och hur löneredovisningen förändras på finska jobbsajter.",
  },

  "/en/resources/how-to-use-linkedin-to-find-a-job-in-finland": {
    title: "LinkedIn Job Search in Finland | How to Use It Effectively | JobCrawls",
    description:
      "LinkedIn covers only a fraction of open roles in Finland. Here is how to use it strategically alongside JobCrawls to search the full Finnish job market and not miss roles posted only on local boards.",
  },

  "/fi/tyoelama/linkedin-tyonhaku-suomessa": {
    title: "LinkedIn-työnhaku Suomessa | Näin käytät sitä | JobCrawls",
    description:
      "LinkedIn kattaa vain osan Suomen avoimista työpaikoista. Näin käytät sitä JobCrawlsin rinnalla, jotta näet koko työmarkkinan etkä missaa paikallisia ilmoituksia.",
  },

  "/sv/arbetsliv/linkedin-jobsok-i-finland": {
    title: "LinkedIn-jobbsök i Finland | Hur du använder det effektivt | JobCrawls",
    description:
      "LinkedIn täcker bara en del av de öppna jobben i Finland. Här är hur du använder det strategiskt tillsammans med JobCrawls för att söka hela den finska arbetsmarknaden.",
  },

  "/en/resources/how-to-write-a-cv-for-a-finnish-employer": {
    title: "CV for a Finnish Employer | Format, Length and What to Include | JobCrawls",
    description:
      "No photo, one page, honest language levels. Finnish CV norms are specific and recruiters notice when you get them wrong. Length, structure, and what to leave out so your application stands out.",
  },

  "/fi/tyoelama/ansioluettelo-suomalaiselle-tyonantajalle": {
    title: "Ansioluettelo suomalaiselle työnantajalle | Vinkit | JobCrawls",
    description:
      "Suomalainen työnantaja odottaa selkeää ja tiivistä ansioluetteloa. Käymme läpi pituuden, rakenteen, mitä kannattaa jättää pois ja miten räätälöit hakemuksesi.",
  },

  "/sv/arbetsliv/cv-for-finsk-arbetsgivare": {
    title: "CV för finsk arbetsgivare | Format, längd och innehåll | JobCrawls",
    description:
      "Finska arbetsgivare förväntar sig ett rent, kortfattat CV i ett specifikt format. Vi går igenom längd, struktur, vad du ska utelämna och hur du skräddarsyr ditt CV för finska arbetsgivare.",
  },

  "/en/resources/finland-job-market-march-2026": {
    title: "Finland Job Market August 2026 | Hiring Trends and Open Roles | JobCrawls",
    description:
      "A data-driven look at Finland's job market in August 2026. Which sectors are growing, where hiring is slowing, and what candidates across Finland are actually competing for.",
  },

  "/fi/tyoelama/suomen-tyomarkkinat-maaliskuu-2026": {
    title: "Suomen työmarkkinat | Alat, palkat ja etätyö | JobCrawls",
    description:
      "Rakentaminen ja ammattityöt on suurin ala Suomen yli 18 300 ilmoituksesta elokuussa 2026. Erittely aloittain, palkat kokemuksen mukaan ja etätyön osuus.",
  },

  "/sv/arbetsliv/finlands-arbetsmarknad-mars-2026": {
    title: "Finlands arbetsmarknad | Branscher och löner | JobCrawls",
    description:
      "Bygg & hantverksyrken leder Finlands 18 300+ aktiva annonser i augusti 2026. Full uppdelning per bransch, medianlön per senioritet och distansarbets­frekvenser.",
  },

  "/en/resources/helsinki-job-market-2026": {
    title: "Helsinki Job Market 2026 | Sectors Hiring and Salary Benchmarks | JobCrawls",
    description:
      "Helsinki dominates Finnish hiring. Which sectors are actually growing, what they pay, and where the open roles are, pulled from company career pages and Työmarkkinatori so you don't miss anything.",
  },

  "/fi/tyoelama/helsingin-tyomarkkinat-2026": {
    title: "Helsingin työmarkkinat 2026 | Alat ja palkat | JobCrawls",
    description:
      "Katsaus Helsingin työmarkkinoihin 2026: eniten rekrytoivat alat, palkkavertailut yleisimmille rooleille ja avoimet työpaikat urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/helsingfors-arbetsmarknad-2026": {
    title: "Helsingfors arbetsmarknad 2026 | Rekryterande sektorer och loner | JobCrawls",
    description:
      "En detaljerad bild av Helsingfors arbetsmarknad 2026. Topprekryterande sektorer, lönebenchmarks för viktiga roller och öppna tjänster från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/tampere-job-market-2026": {
    title: "Tampere Job Market 2026 | Engineering, Tech and What's Hiring | JobCrawls",
    description:
      "Engineering, manufacturing and tech drive Tampere's hiring. We dug through the listings to find what's in demand, what it pays, and where the roles actually live, all in one search.",
  },

  "/fi/tyoelama/tampereen-tyomarkkinat-2026": {
    title: "Tampereen työmarkkinat 2026 | Alat ja palkat | JobCrawls",
    description:
      "Tampereen työmarkkinat 2026: kysyntää insinööri-, valmistus-, teknologia- ja terveysaloilla. Palkkavertailut ja avoimet paikat urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/tammerfors-arbetsmarknad-2026": {
    title: "Tammerfors arbetsmarknad 2026 | Ingenjör, teknik och trender | JobCrawls",
    description:
      "En datadriven bild av Tammerfors arbetsmarknad 2026. Ingenjör, tillverkning, teknik och vård i efterfrågan. Lönebenchmarks och öppna tjänster aggregerade från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/technology-salary-guide-finland-2026-03": {
    title: "Tech Salary Guide Finland March 2026 | Real Pay Data | JobCrawls",
    description:
      "Salary benchmarks for software engineers, data scientists, DevOps and other tech roles in Finland, based on live job listing data from company career pages and Työmarkkinatori as of March 2026.",
  },

  "/fi/tyoelama/teknologia-alan-palkkaopas-suomi-maaliskuu-2026": {
    title: "Teknologia-alan palkkaopas 2026 | Oikeat palkat | JobCrawls",
    description:
      "Palkat ohjelmistokehittäjille, data-analyytikoille, DevOps-insinööreille ja muille teknologia-alan rooleille. Data urasivuilta ja Työmarkkinatorilta, 3/2026.",
  },

  "/sv/arbetsliv/teknikloner-finland-mars-2026": {
    title: "Tekniklöner Finland mars 2026 | Verklig lönedata | JobCrawls",
    description:
      "Lönebenchmarks för mjukvaruutvecklare, datavetare, DevOps och andra tekniska roller i Finland, baserade på live-jobbannonser från företags karriärsidor och Työmarkkinatori, mars 2026.",
  },

  "/en/resources/jobs-in-finland/technology-salary-guide-finland-2026-04": {
    title: "Tech Salary Guide Finland April 2026 | Updated Pay Benchmarks | JobCrawls",
    description:
      "Updated salary benchmarks for tech roles in Finland as of April 2026. Software, data, cloud and security roles covered using live data from company career pages and Työmarkkinatori.",
  },

  "/fi/tyoelama/tyot-suomessa/teknologia-palkkaopas-suomi-2026-04": {
    title: "Teknologia-alan palkat 4/2026 | Päivitetty opas | JobCrawls",
    description:
      "Palkkavertailut teknologia-alan rooleille Suomessa huhtikuussa 2026: ohjelmistokehitys, data, pilvi ja tietoturva. Tuore data urasivuilta ja Työmarkkinatorilta.",
  },

  "/sv/arbetsliv/jobb-i-finland/teknologi-loeneguide-finland-2026-04": {
    title: "Tekniklöneguide Finland april 2026 | Uppdaterade lönebenchmarks | JobCrawls",
    description:
      "Uppdaterade lönebenchmarks för tekniska roller i Finland, april 2026. Mjukvara, data, moln och säkerhet täckt med live-data från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/jobs-in-finland/finland-hidden-job-market-networking-2026-03": {
    title: "Finland's Hidden Job Market | Networking and Unlisted Roles | JobCrawls",
    description:
      "Up to half of Finnish jobs are never publicly listed. Here is how to tap into the hidden job market through networking, while using JobCrawls to cover every role that is publicly posted.",
  },

  "/fi/tyoelama/tyot-suomessa/suomen-piilotyomarkkinat-verkostoituminen-2026-03": {
    title: "Suomen piilotyömarkkinat | Verkostoituminen | JobCrawls",
    description:
      "Jopa puolta suomalaisista työpaikoista ei julkaista avoimesti. Näin pääset piilotyömarkkinoille verkostoitumalla, kun JobCrawls hoitaa julkiset ilmoitukset.",
  },

  "/sv/arbetsliv/jobb-i-finland/finlands-dolda-arbetsmarknad-2026-03": {
    title: "Finlands dolda arbetsmarknad | Nätverkande och oannonserade jobb | JobCrawls",
    description:
      "Upp till hälften av finska jobb annonseras aldrig offentligt. Här är hur du kommer åt den dolda arbetsmarknaden via nätverkande medan JobCrawls täcker alla offentligt annonserade roller.",
  },

  "/en/resources/summer-jobs": {
    title: "Summer Jobs in Finland | All in One Search | JobCrawls",
    description:
      "Student roles, seasonal work and part-time positions across Helsinki, Tampere, Turku and beyond. Instead of checking every board separately, see summer jobs from company career pages and Työmarkkinatori in one search.",
  },

  "/fi/tyoelama/kesatyot": {
    title: "Kesätyöt Suomessa | Kesätyöpaikat yhdessä haussa | JobCrawls",
    description:
      "Kesätyöpaikat Suomesta urasivuilta ja Työmarkkinatorilta yhdellä haulla: opiskelijapaikat, kausityöt ja osa-aikatyöt Helsingissä, Tampereella, Turussa ja muualla.",
  },

  "/sv/arbetsliv/sommarjobb-i-finland": {
    title: "Sommarjobb i Finland | Allt på ett ställe | JobCrawls",
    description:
      "Hitta sommarjobb i Finland från företags karriärsidor och Työmarkkinatori i en sökning. Studentjobb, säsongsjobb och deltidstjänster i Helsingfors, Tammerfors, Åbo och mer, uppdaterade dagligen.",
  },

  "/en/resources/jobs-in-finland/cool-finnish-tech-companies-english": {
    title: "Finnish Tech Companies That Work in English | Where to Apply | JobCrawls",
    description:
      "You don't need Finnish to work in Finnish tech. Profiles of the best companies hiring in English: what they build, their vibe, and live open roles all in one place instead of hunting each site separately.",
  },

  "/fi/tyoelama/suomalaiset-tech-yritykset-tyokielena-englanti": {
    title: "Suomalaiset tech-yritykset | Englanti työkielenä | JobCrawls",
    description:
      "Suomalaiset teknologiayritykset, jotka rekrytoivat ilman suomen kielen vaatimusta: yritysprofiilit, mitä ne rakentavat ja linkit avoimiin paikkoihin.",
  },

  "/sv/arbetsliv/finska-techbolag-engelska-arbetssprak": {
    title: "Finska techbolag med engelska som arbetsspråk | Var du söker | JobCrawls",
    description:
      "De bästa finska techbolagen som rekryterar utan krav på finska språkkunskaper. Företagsprofiler, vad de bygger och länkar till öppna roller aggregerade från företags karriärsidor och Työmarkkinatori.",
  },

  "/en/resources/jobs-in-finland/highest-salaries-finland": {
    title: "Highest Salaries in Finland 2026: City-by-City Comparison | JobCrawls",
    description:
      "Helsinki, Espoo, Tampere, Turku, and Oulu now sit within a few hundred euros of each other on advertised pay. Compare salaries, rent, and real purchasing power across all five.",
  },

  "/fi/tyoelama/korkeimmat-palkat-suomessa-alueittain": {
    title: "Suomen korkeimmat palkat 2026 | Kaupunkivertailu | JobCrawls",
    description:
      "Helsinki, Espoo, Tampere, Turku ja Oulu ovat palkoissa enää muutaman sadan euron päässä toisistaan, yleensä 2 707-3 388 €/kk. Vertaa palkkoja, vuokria ja ostovoimaa.",
  },

  "/sv/arbetsliv/hogsta-lonerna-finland-regioner": {
    title: "Finlands högsta löner 2026: stad för stad-jämförelse | JobCrawls",
    description:
      "Helsingfors, Esbo, Tammerfors, Åbo och Uleåborg ligger nu inom några hundra euro från varandra i annonserad lön, oftast 2 707-3 388 €/mån. Jämför löner, hyror och köpkraft i alla fem.",
  },

  "/en/resources/jobs-in-finland/best-job-websites-finland": {
    title: "Best Job Websites in Finland | Compared and Ranked | JobCrawls",
    description:
      "Duunitori, Jobly, Työmarkkinatori, LinkedIn, Indeed, Oikotie: which one actually has the roles you want? We compared them by volume, industry coverage, and which ones are worth your time.",
  },

  "/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa": {
    title: "Parhaat työnhakusivustot Suomessa | Vertailu | JobCrawls",
    description:
      "Suomalaiset työnhakusivustot vertailussa: Duunitori, Jobly, Työmarkkinatori, LinkedIn, Indeed ja muut. Millä on eniten ilmoituksia ja mikä sopii sinun alallesi?",
  },

  "/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland": {
    title: "Bästa jobbsajterna i Finland | Jämförda och rankade | JobCrawls",
    description:
      "En komplett jämförelse av finska jobbsajter: Duunitori, Jobly, Työmarkkinatori, LinkedIn, Indeed och fler. Vilka sajter har flest annonser och vilken passar bäst för din bransch? Eller använd bara JobCrawls.",
  },
} as const satisfies Record<string, UrlStaticSeoMeta>;
